import React, { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { getDomainPageFeedback, updateDomainPageFeedback } from '../api';
import Alert from '../ui/Alert';
import Spinner from '../ui/Spinner';
import EmptyState from '../ui/EmptyState';
import { relativeTime } from '../domainMetrics';
import { helpfulShare } from '../emailHelpers';

const errText = (err, fallback) => {
  const d = err?.response?.data?.detail;
  return typeof d === 'string' ? d : fallback;
};

const SORTS = [
  ['votes', 'Most votes'],
  ['down', 'Most 👎'],
  ['worst', 'Least helpful'],
];

const sorted = (pages, sort) => {
  const rows = [...pages];
  if (sort === 'down') rows.sort((a, b) => b.down - a.down || b.up - a.up);
  else if (sort === 'worst') {
    rows.sort((a, b) => (helpfulShare(a) ?? 1) - (helpfulShare(b) ?? 1) || b.down - a.down);
  }
  return rows; // 'votes': the server's order
};

/* Site settings panel: the article thumbs widget's on/off switch and each
   page's tally (counted by the sites in domains/<id>/page_votes). Notes
   readers sent are in the inbox's Reader feedback view. */
export default function ReaderFeedbackSettings({ domainId, liveUrl }) {
  const [data, setData] = useState(null);
  const [error, setError] = useState(null);
  const [saving, setSaving] = useState(false);
  const [sort, setSort] = useState('votes');

  useEffect(() => {
    let cancelled = false;
    getDomainPageFeedback(domainId)
      .then((d) => { if (!cancelled) setData(d); })
      .catch((err) => { if (!cancelled) setError(errText(err, 'Failed to load reader feedback')); });
    return () => { cancelled = true; };
  }, [domainId]);

  const toggle = async () => {
    setSaving(true);
    setError(null);
    try {
      setData(await updateDomainPageFeedback(domainId, !data.enabled));
    } catch (err) {
      setError(errText(err, 'Failed to save'));
    } finally {
      setSaving(false);
    }
  };

  if (!data && !error) return <Spinner label="Loading reader feedback…" />;
  if (!data) return <Alert type="error">{error}</Alert>;

  const { totals } = data;
  const share = helpfulShare(totals);

  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: '12px' }}>
      <Alert type="error" onDismiss={() => setError(null)}>{error}</Alert>
      <label style={{ display: 'flex', alignItems: 'center', gap: '8px', fontSize: '14px', margin: 0 }}>
        <input type="checkbox" checked={data.enabled} disabled={saving} onChange={toggle}
               style={{ width: '16px', height: '16px', accentColor: 'var(--color-primary)' }} />
        Show “Was this article helpful?” under articles
        <span className="muted small">(the site picks this up within a few minutes)</span>
      </label>

      <div className="muted small">
        {totals.up} 👍 · {totals.down} 👎
        {share != null && ` · ${Math.round(share * 100)}% helpful`}
        {' · '}
        <Link to={`/emails?filter=feedback&domain=${encodeURIComponent(domainId)}`}>
          {totals.messages} note{totals.messages !== 1 ? 's' : ''} in the inbox →
        </Link>
      </div>

      {data.pages.length === 0 ? (
        <EmptyState title="No votes yet" message="Votes appear here as readers use the thumbs under each article." />
      ) : (
        <>
          <div className="chip-group" role="tablist" aria-label="Sort pages">
            {SORTS.map(([key, label]) => (
              <button key={key} type="button" role="tab" aria-selected={sort === key}
                      className={`chip${sort === key ? ' active' : ''}`} onClick={() => setSort(key)}>
                {label}
              </button>
            ))}
          </div>
          <div className="table-wrap">
            <table className="data-table">
              <thead>
                <tr>
                  <th>Page</th>
                  <th style={{ textAlign: 'right' }}>👍</th>
                  <th style={{ textAlign: 'right' }}>👎</th>
                  <th style={{ textAlign: 'right' }}>Helpful</th>
                  <th style={{ textAlign: 'right' }}>Notes</th>
                  <th>Last vote</th>
                </tr>
              </thead>
              <tbody>
                {sorted(data.pages, sort).map((p) => {
                  const s = helpfulShare(p);
                  return (
                    <tr key={p.page_id}>
                      <td>
                        <a href={`${liveUrl}/${p.slug}/`} target="_blank" rel="noopener noreferrer">
                          <strong>{p.title || p.slug}</strong>
                        </a>
                        <div className="muted mono" style={{ fontSize: '12px' }}>/{p.slug}</div>
                      </td>
                      <td style={{ textAlign: 'right' }}>{p.up}</td>
                      <td style={{ textAlign: 'right' }}>{p.down}</td>
                      <td style={{ textAlign: 'right' }}>{s == null ? '–' : `${Math.round(s * 100)}%`}</td>
                      <td style={{ textAlign: 'right' }}>{p.messages || ''}</td>
                      <td className="muted small">{p.updated_at ? relativeTime(p.updated_at) : ''}</td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        </>
      )}
    </div>
  );
}
